import React from 'react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { Tooltip } from '../UI/Tooltip';
import type { DisclosureCategory, DisclosureDefinition } from '../../data/disclosures';

/**
 * One colour per disclosure CATEGORY, so a reader learns four meanings rather
 * than thirteen. Deliberately avoids green — green is reserved platform-wide
 * for account verification (Verified Reviewer) and must not be reused for a
 * commercial relationship.
 */
const CATEGORY_CLASSES: Record<DisclosureCategory, {
  light: string;
  dark: string;
}> = {
  purchase: {
    light: 'bg-gray-100 text-gray-700',
    dark: 'bg-gray-700/50 text-gray-300'
  },
  gifted: {
    light: 'bg-blue-100 text-blue-800',
    dark: 'bg-blue-900/30 text-blue-300'
  },
  paid: {
    light: 'bg-amber-100 text-amber-900',
    dark: 'bg-amber-900/30 text-amber-300'
  },
  interest: {
    light: 'bg-purple-100 text-purple-800',
    dark: 'bg-purple-900/30 text-purple-300'
  },
  organic: {
    light: 'bg-slate-100 text-slate-600',
    dark: 'bg-slate-800 text-slate-400'
  }
};
export const useDisclosureCategoryClasses = () => {
  const dk = useDarkClasses();
  return (category: DisclosureCategory) => {
    const entry = CATEGORY_CLASSES[category] || CATEGORY_CLASSES.purchase;
    return dk.isDark ? entry.dark : entry.light;
  };
};
interface DisclosureBadgeProps {
  disclosure: DisclosureDefinition;
  /** Use the compact label — for table cells and image overlays. */
  compact?: boolean;
  /** Suppress the tooltip where the explanation is shown elsewhere. */
  interactive?: boolean;
}

/**
 * A single disclosure pill. Compact and rounded to match the platform's trope
 * and content-tag language, and intentionally quieter than the review text it
 * sits above — disclosure must inform without overpowering the review.
 */
export const DisclosureBadge: React.FC<DisclosureBadgeProps> = ({
  disclosure,
  compact = false,
  interactive = true
}) => {
  const classesFor = useDisclosureCategoryClasses();
  const pill = <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${classesFor(disclosure.category)} ${interactive ? 'cursor-help' : ''}`} data-id="element-1482" data-mp="3w2e0emx4nvvs">
      {compact ? disclosure.shortLabel : disclosure.label}
    </span>;
  if (!interactive) return pill;
  return <Tooltip content={disclosure.explanation} label={`${disclosure.label} — what this means`} data-id="element-1483" data-mp="4quq3850oubx">
      {pill}
    </Tooltip>;
};
export default DisclosureBadge;